'use client';

import type { ArchiMateElement, ArchiMateRelationship, ArchiMateDiagram } from '@/lib/archimate/types';
import { getElementMetadata } from '@/lib/archimate/elements';

interface InspectModalProps {
  element: ArchiMateElement;
  diagram: ArchiMateDiagram;
  onClose: () => void;
}

export function InspectModal({ element, diagram, onClose }: InspectModalProps) {
  const metadata = getElementMetadata(element.type);

  // Find all relationships where this element is source or target
  const incomingRelationships = diagram.relationships.filter(
    (rel) => rel.targetId === element.id
  );
  const outgoingRelationships = diagram.relationships.filter(
    (rel) => rel.sourceId === element.id
  );

  const getElementName = (elementId: string) => {
    const el = diagram.elements.find((e) => e.id === elementId);
    return el?.name || 'Unknown Element';
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-background border-2 border-accent rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="border-b border-border px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-foreground">{element.name}</h2>
            <div className="text-sm text-foreground-tertiary capitalize">
              {metadata?.name || element.type} · {element.layer} layer
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-foreground-tertiary hover:text-foreground text-xl"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Basic Properties */}
          <div>
            <h3 className="text-sm font-semibold text-accent uppercase tracking-wide mb-3">
              Properties
            </h3>
            <div className="bg-surface rounded p-4 space-y-2">
              <div className="grid grid-cols-3 gap-2 text-sm">
                <span className="text-foreground-tertiary">Type:</span>
                <span className="col-span-2 text-foreground capitalize">
                  {metadata?.name || element.type}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-sm">
                <span className="text-foreground-tertiary">Layer:</span>
                <span className="col-span-2 text-foreground capitalize">
                  {element.layer}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-sm">
                <span className="text-foreground-tertiary">Position:</span>
                <span className="col-span-2 text-foreground">
                  x: {Math.round(element.x)}, y: {Math.round(element.y)}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-sm">
                <span className="text-foreground-tertiary">Size:</span>
                <span className="col-span-2 text-foreground">
                  {element.width || 120} × {element.height || 60}
                </span>
              </div>
              {element.masterId && (
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <span className="text-foreground-tertiary">Component:</span>
                  <span className="col-span-2 text-foreground">
                    {element.isMaster ? 'Master' : 'Instance (linked)'}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Documentation */}
          {element.documentation && (
            <div>
              <h3 className="text-sm font-semibold text-accent uppercase tracking-wide mb-3">
                Documentation
              </h3>
              <div className="bg-surface rounded p-4 text-sm text-foreground">
                {element.documentation}
              </div>
            </div>
          )}

          {/* Custom Properties */}
          {element.properties && Object.keys(element.properties).length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-accent uppercase tracking-wide mb-3">
                Custom Properties
              </h3>
              <div className="bg-surface rounded p-4 space-y-2">
                {Object.entries(element.properties).map(([key, value]) => (
                  <div key={key} className="grid grid-cols-3 gap-2 text-sm">
                    <span className="text-foreground-tertiary capitalize">{key}:</span>
                    <span className="col-span-2 text-foreground">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Incoming Relationships */}
          <div>
            <h3 className="text-sm font-semibold text-accent uppercase tracking-wide mb-3">
              Incoming Relationships ({incomingRelationships.length})
            </h3>
            {incomingRelationships.length > 0 ? (
              <div className="bg-surface rounded p-4 space-y-2">
                {incomingRelationships.map((rel) => (
                  <div
                    key={rel.id}
                    className="flex items-center gap-3 p-2 bg-background rounded text-sm"
                  >
                    <span className="text-foreground-tertiary">←</span>
                    <span className="flex-1 text-foreground">
                      <span className="font-medium">{getElementName(rel.sourceId)}</span>
                      <span className="text-foreground-tertiary mx-2">·</span>
                      <span className="text-accent capitalize">
                        {rel.type.replace(/-/g, ' ')}
                      </span>
                      {rel.name && (
                        <>
                          <span className="text-foreground-tertiary mx-2">·</span>
                          <span className="text-foreground-secondary">{rel.name}</span>
                        </>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-surface rounded p-4 text-sm text-foreground-muted text-center">
                No incoming relationships
              </div>
            )}
          </div>

          {/* Outgoing Relationships */}
          <div>
            <h3 className="text-sm font-semibold text-accent uppercase tracking-wide mb-3">
              Outgoing Relationships ({outgoingRelationships.length})
            </h3>
            {outgoingRelationships.length > 0 ? (
              <div className="bg-surface rounded p-4 space-y-2">
                {outgoingRelationships.map((rel) => (
                  <div
                    key={rel.id}
                    className="flex items-center gap-3 p-2 bg-background rounded text-sm"
                  >
                    <span className="text-foreground-tertiary">→</span>
                    <span className="flex-1 text-foreground">
                      <span className="font-medium">{getElementName(rel.targetId)}</span>
                      <span className="text-foreground-tertiary mx-2">·</span>
                      <span className="text-accent capitalize">
                        {rel.type.replace(/-/g, ' ')}
                      </span>
                      {rel.name && (
                        <>
                          <span className="text-foreground-tertiary mx-2">·</span>
                          <span className="text-foreground-secondary">{rel.name}</span>
                        </>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-surface rounded p-4 text-sm text-foreground-muted text-center">
                No outgoing relationships
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-border px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-accent text-white rounded hover:bg-accent-hover transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
